/**
 * Grain matrix for the Sales popout: metric rows x time-grain columns, mirroring
 * `DealerInsightsPopout`'s tables in Client View so the two tabs read the same.
 */
import { useState } from 'react';
import type { SalesWindowMetrics } from '../../api/types';
import { getCountTrend, getRateTrend, getVolumeTrend, formatPercent, formatCount, isMmrRed, MIN_PCT_BASE_UNITS, type Trend } from './salesViewFormat';
import { METRIC_TIPS } from './salesMetricTips';

type WindowCd = string;

interface GrainColumn {
  key: string;
  label: string;
  sub: string;
  curr: WindowCd;
  comparisons: { label: string; window: WindowCd }[];
  tooltip: string;
}

/**
 * Ordered shortest span to longest, matching Client View's `GRAIN_COLUMNS`.
 *
 * Day uses PD (yesterday), not CD. The stats build runs overnight, so the current
 * day is always empty and a "Day" column reading zero for every client would be a
 * bug report, not a metric.
 *
 * W4/W8P are deliberately ABSENT. They are 4- and 8-week TOTALS, not to-date
 * windows; dropping them into this matrix next to a week-to-date column makes
 * every client look like it quadrupled. They belong to the drift signal, which
 * normalises them to a weekly rate first.
 */
const GRAIN_COLUMNS: GrainColumn[] = [
  {
    key: 'day', label: 'Day', sub: 'yesterday', curr: 'PD',
    comparisons: [{ label: 'vs prev day', window: 'PD2' }],
    tooltip: 'Yesterday vs the day before. Today is excluded — the stats build runs overnight, so it is always empty.',
  },
  {
    key: 'week', label: 'Week', sub: 'to date', curr: 'CW',
    comparisons: [{ label: 'vs prior wk', window: 'PW' }],
    tooltip: 'This week (Mon through today) vs the last full week. Partial by definition.',
  },
  {
    key: 'month', label: 'Month', sub: 'to date', curr: 'CM',
    comparisons: [{ label: 'vs prior mo', window: 'PM' }],
    tooltip: 'This month to date vs the last full month.',
  },
  {
    key: 'quarter', label: 'Quarter', sub: 'to date', curr: 'CQ',
    comparisons: [
      { label: 'vs prior Q', window: 'PQ' },
      { label: 'vs LY', window: 'CQLY' },
    ],
    tooltip: 'Calendar quarter to date against the last full quarter and the same quarter last year.',
  },
  {
    key: 'period', label: 'Period', sub: 'rolling 3mo', curr: 'PERIOD',
    comparisons: [{ label: 'vs prior', window: 'PRIOR_PERIOD' }],
    tooltip: 'Rolling 3 months through today vs the 3 before it. This is the window the rate signals score on.',
  },
  {
    key: 'ytd', label: 'YTD', sub: 'to date', curr: 'CYTD',
    comparisons: [{ label: 'vs last YTD', window: 'PYTD' }],
    tooltip: 'Jan 1 through today vs the same span last year.',
  },
];

/** Same three Client View opens with: enough to read a trend, few enough to fit. */
const DEFAULT_GRAIN_KEYS = ['week', 'month', 'period'];

type MetricKind = 'count' | 'rate' | 'days' | 'money' | 'ratio';

interface MetricRow {
  label: string;
  field: keyof SalesWindowMetrics;
  kind: MetricKind;
  tooltip?: string;
  /** True when LOWER is better, so the trend colour flips. Days-to-sell falling
   *  is good news and must not render red. */
  invert?: boolean;
}

const SELL_METRICS: MetricRow[] = [
  { label: 'Listings', field: 'lst_total_listings', kind: 'count',
    tooltip: 'Listings that went up in the window — first offerings plus re-lists, so a car listed twice counts twice.' },
  { label: 'Sales', field: 'lst_total_sales', kind: 'count',
    tooltip: 'Sales on the listings that went up in the window, before back-outs.' },
  { label: 'Net sales', field: 'lst_total_net_sales', kind: 'count', tooltip: 'Sales net of arbitration and unwinds (back-outs).' },
  { label: 'Effectiveness (sell-through)', field: 'listings_funnel_conversion', kind: 'rate', tooltip: METRIC_TIPS.effectiveness },
  { label: 'Offering conversion', field: 'offerings_funnel_conversion', kind: 'rate', tooltip: METRIC_TIPS.offeringConversion },
];

const BUY_METRICS: MetricRow[] = [
  { label: 'Purchases', field: 'buy_total_purchases', kind: 'count',
    tooltip: 'Cars this client bought, by sale-close day, before back-outs.' },
  { label: 'Net purchases', field: 'buy_total_net_purchases', kind: 'count',
    tooltip: 'Cars bought, net of back-outs. This is the buy volume the health score reads.' },
];

const OFFSITE_METRICS: MetricRow[] = [
  { label: 'Inspections', field: 'mmi_insp_total_inspections', kind: 'count',
    tooltip: 'MMI inspections completed, by inspection day. A car inspected again later counts again.' },
  { label: 'Sales from inspected', field: 'mmi_insp_sales_from_inspected_vins', kind: 'count',
    tooltip: 'Sales closing in the window on any car this client had MMI-inspected. Gross, like Plan My Week.' },
  { label: 'Offsite conversion', field: 'offsite_conversion', kind: 'rate', tooltip: METRIC_TIPS.offsiteConversion },
];

/** How long each stage of the funnel takes. Fewer days is better throughout, so
 *  the trend arrows read inverted — see `invert` on the row. */
const TIMING_METRICS: MetricRow[] = [
  { label: 'Offering → sale', field: 'lst_avg_days_offering_to_sell', kind: 'days', invert: true,
    tooltip: 'Avg days from a listing first going up to it selling. Time on market.' },
  { label: 'Offering → sale (cycle)', field: 'ofr_avg_days_offering_to_sell', kind: 'days', invert: true,
    tooltip: 'Same measure at the sell-cycle grain rather than the listing grain.' },
  { label: 'Inspection → listed', field: 'mmi_insp_avg_days_inspection_to_offering', kind: 'days', invert: true,
    tooltip: 'Avg days from an MMI inspection to the car first being offered. Time to list.' },
  { label: 'Inspection → sale', field: 'mmi_insp_avg_days_inspection_to_sell', kind: 'days', invert: true,
    tooltip: 'Avg days from inspection all the way to sale.' },
  { label: 'Times offered per car', field: 'ofr_avg_times_offered', kind: 'ratio', invert: true,
    tooltip: 'Listings per sell cycle. Above ~2 means cars are going up repeatedly before they move.' },
  { label: 'Re-list events', field: 'ofr_relists', kind: 'count', invert: true,
    tooltip: 'Times a car went back up after its first offering. Fewer is better.' },
];

/** What they price at against market. Ratios are volume-weighted server-side. */
const PRICING_METRICS: MetricRow[] = [
  { label: 'Avg floor price', field: 'lst_avg_listed_floor_price', kind: 'money',
    tooltip: 'Weighted avg floor set across listings.' },
  { label: 'Avg MMR at listing', field: 'lst_avg_listed_mmr', kind: 'money',
    tooltip: 'Average national MMR when the cars were listed, weighted across listings.' },
  { label: 'Floor as % of MMR', field: 'floor_to_mmr', kind: 'rate',
    tooltip: 'How aggressively they floor. Below 100% means floors sit under market.' },
  { label: 'Avg sold price', field: 'lst_avg_purchase_price', kind: 'money',
    tooltip: 'Average sale price across the listings that sold.' },
  { label: 'Avg MMR at sale', field: 'lst_avg_sold_mmr', kind: 'money',
    tooltip: 'Average MMR at the time of sale, across the listings that sold.' },
  { label: 'MMR retention', field: 'sold_to_mmr', kind: 'rate',
    tooltip: 'Sold price ÷ MMR at sale, volume-weighted — what they realise against market. 100% = sold at MMR. Over 115% renders red, matching the Manheim Dealer Performance convention.' },
];

function trendFor(metric: MetricRow, current: number | null | undefined, prior: number | null | undefined): Trend {
  let t = metric.kind === 'rate'
    ? getRateTrend(current, prior)
    : metric.kind === 'count'
      ? getCountTrend(current, prior)
      : getVolumeTrend(current, prior);
  // Dollars, days and ratios: the change in their own unit rides beside the percent.
  if ((metric.kind === 'money' || metric.kind === 'days' || metric.kind === 'ratio')
      && current != null && prior != null && prior !== 0 && t.direction !== 'new') {
    t = { ...t, label: `${t.label} (${signedDelta(metric.kind, current - prior)})` };
  }
  if (!metric.invert) return t;
  // Days-to-sell falling is GOOD. Flip only the colour direction; the label
  // already carries the sign and must not be rewritten.
  if (t.direction === 'up') return { ...t, direction: 'down' };
  if (t.direction === 'down') return { ...t, direction: 'up' };
  return t;
}

function fmt(kind: MetricKind, value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (kind === 'rate') return formatPercent(value);
  if (kind === 'money') return `$${Math.round(value).toLocaleString()}`;
  if (kind === 'days') return `${value.toFixed(1)}d`;
  if (kind === 'ratio') return value.toFixed(2);
  return formatCount(value);
}

function signedDelta(kind: MetricKind, delta: number): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  return `${sign}${fmt(kind, Math.abs(delta))}`;
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "Aug 1 – Aug 21", or a single date when the window is one day. */
function rangeOf(w: SalesWindowMetrics | undefined): string | null {
  if (!w?.win_start_date || !w?.win_end_date) return null;
  const from = shortDate(w.win_start_date);
  const to = shortDate(w.win_end_date);
  return from === to ? from : `${from} – ${to}`;
}

/** Static explanation plus the dates this client's row actually covers, so the
 *  header is not asking the rep to trust an abstract window name. */
function tooltipFor(col: GrainColumn, byWindow: Map<string, SalesWindowMetrics>): string {
  const lines = [col.tooltip];
  const curr = rangeOf(byWindow.get(col.curr));
  if (curr) lines.push(`${col.label}: ${curr}`);
  for (const c of col.comparisons) {
    const r = rangeOf(byWindow.get(c.window));
    if (r) lines.push(`${c.label}: ${r}`);
  }
  return lines.join('\n');
}

function TrendMark({ trend }: { trend: Trend }) {
  if (trend.direction === 'new') return <span className="sv-card-change sv-card-change-new">New</span>;
  const glyph = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '';
  return (
    <span className={`sv-card-change sv-card-change-${trend.direction}`}>
      {glyph} {trend.label}
    </span>
  );
}

/** What one comparison cell means, in words: both numbers, and which rule produced the label. */
function cmpTip(
  metric: MetricRow,
  current: number | null | undefined,
  prior: number | null | undefined,
  head: string,
): string {
  if (current == null || prior == null) return `${head}. No comparison — one side has no data.`;
  const now = fmt(metric.kind, current);
  const then = fmt(metric.kind, prior);
  let body: string;
  if (metric.kind === 'rate') {
    body = `${now} now vs ${then}. Rates move in percentage points, not percent.`;
  } else if (metric.kind === 'count' && prior === 0) {
    body = current > 0 ? `${now} now vs none before, so it reads New.` : 'None in either window.';
  } else if (metric.kind === 'count' && Math.abs(prior) < MIN_PCT_BASE_UNITS) {
    body = `${now} now vs ${then}. Under ${MIN_PCT_BASE_UNITS} units before, so only the unit change is shown — one car is not a trend.`;
  } else if (metric.kind === 'count') {
    body = `${now} now vs ${then}. Percent change, with the unit change in brackets.`;
  } else {
    body = `${now} now vs ${then}. Percent change, with the change itself in brackets.`;
  }
  if (metric.invert) body += ' Lower is better here, so green means it fell.';
  return `${head}. ${body}`;
}

function GrainCell({ metric, col, byWindow }: {
  metric: MetricRow;
  col: GrainColumn;
  byWindow: Map<string, SalesWindowMetrics>;
}) {
  const curr = byWindow.get(col.curr)?.[metric.field] as number | null | undefined;
  const red = metric.field === 'sold_to_mmr' && isMmrRed(curr);
  return (
    <td className="sv-grain-cell">
      <div className={`sv-grain-value${red ? ' sv-mmr-red' : ''}`}>{fmt(metric.kind, curr)}</div>
      {col.comparisons.map((c) => {
        const prior = byWindow.get(c.window)?.[metric.field] as number | null | undefined;
        const range = rangeOf(byWindow.get(c.window));
        return (
          <div key={c.window} className="sv-grain-cmp"
            title={cmpTip(metric, curr, prior, range ? `${c.label}: ${range}` : c.label)}>
            <span className="sv-grain-cmp-label">{c.label}</span>
            <TrendMark trend={trendFor(metric, curr, prior)} />
          </div>
        );
      })}
    </td>
  );
}

function GrainTable({ title, metrics, cols, byWindow, headerExtra }: {
  title: string;
  metrics: MetricRow[];
  cols: GrainColumn[];
  byWindow: Map<string, SalesWindowMetrics>;
  headerExtra?: React.ReactNode;
}) {
  return (
    <div className="sv-grain-block">
      <div className="sv-grain-head">
        <h4 className="sv-grain-title">{title}</h4>
        {headerExtra}
      </div>
      <div className="sv-grain-scroll">
        <table className="sv-grain-table">
          <thead>
            <tr>
              <th className="sv-grain-th-metric" />
              {cols.map((c) => (
                <th key={c.key} className="sv-grain-th" title={tooltipFor(c, byWindow)}>
                  {c.label}
                  <span className="sv-grain-th-sub">{rangeOf(byWindow.get(c.curr)) ?? c.sub}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.label}>
                <td className="sv-grain-metric" title={m.tooltip}>{m.label}</td>
                {cols.map((c) => (
                  <GrainCell key={c.key} metric={m} col={c} byWindow={byWindow} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SalesGrainTablesProps {
  windows: SalesWindowMetrics[];
}

/** Sell / buy / offsite across every grain the Parquet carries. */
export function SalesGrainTables({ windows }: SalesGrainTablesProps) {
  const [showAll, setShowAll] = useState(false);
  const byWindow = new Map(windows.map((w) => [w.window_cd, w]));
  const cols = showAll
    ? GRAIN_COLUMNS
    : GRAIN_COLUMNS.filter((g) => DEFAULT_GRAIN_KEYS.includes(g.key));

  const toggle = (
    <button
      type="button"
      className="sv-grain-toggle"
      onClick={() => setShowAll((v) => !v)}
      aria-expanded={showAll}
      title={showAll ? 'Show only Week, Month and Period' : 'Also show Day, Quarter and YTD'}
    >
      {showAll ? '− Fewer grains' : '+ Day · Quarter · YTD'}
    </button>
  );

  if (windows.length === 0) {
    return <div className="sv-popout-empty">No window data for this client.</div>;
  }

  return (
    <div className="sv-grains">
      <GrainTable title="Selling" metrics={SELL_METRICS} cols={cols} byWindow={byWindow} headerExtra={toggle} />
      <GrainTable title="Buying" metrics={BUY_METRICS} cols={cols} byWindow={byWindow} />
      <GrainTable title="Offsite" metrics={OFFSITE_METRICS} cols={cols} byWindow={byWindow} />
      <GrainTable title="Funnel timing" metrics={TIMING_METRICS} cols={cols} byWindow={byWindow} />
      <GrainTable title="Pricing vs market" metrics={PRICING_METRICS} cols={cols} byWindow={byWindow} />
      <p className="sv-grain-note">
        Counts move in percent with the unit change beside it; under {MIN_PCT_BASE_UNITS} units
        only the unit change is shown, because one car is not a trend. Rates move in
        percentage points, so a conversion going
        20% to 10% reads “−10 pts”, not “−50%”. On timing rows a FALLING number is good,
        so the arrows are inverted there. MMR retention and other %MMR figures are volume-weighted, never an
        average of per-car ratios. The 4- and 8-week rolling windows the drift signal
        uses are totals, not to-date windows, so they are not shown here — see
        “Why this client surfaced”.
      </p>
    </div>
  );
}

export default SalesGrainTables;
